package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.SupportMessageRow
import com.arenaproplay.admin.domain.admin.SupportMessagesPage
import com.arenaproplay.admin.domain.me.ChatListItem
import com.arenaproplay.admin.domain.me.ChatListPage
import com.arenaproplay.admin.domain.me.ChatMessage
import com.arenaproplay.admin.domain.me.ChatMessagesPage
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs

// ── Tab enum ──────────────────────────────────────────────────────────────────

private enum class InboxTab(val code: String, val label: String) {
    SUPPORT("support", "Обращения"),
    CHATS("chats", "Чаты");

    companion object {
        fun fromCode(code: String?): InboxTab =
            entries.find { it.code == code } ?: SUPPORT
    }
}

// ── Chat scope ────────────────────────────────────────────────────────────────

private enum class InboxChatScope { MY, ALL }

// ── Constants ─────────────────────────────────────────────────────────────────

private const val INBOX_SUPPORT_PAGE_SIZE = 50
private const val INBOX_CHAT_PAGE_SIZE = 25

// ── Page ──────────────────────────────────────────────────────────────────────

/**
 * Входящие — consolidates Support (Обращения) and Chat (Чаты) as tabs.
 *
 * URL state:
 *   ?tab=support|chats (default support)
 *   Support: ?page=N ?q=text
 *   Chat:    ?scope=all ?page=N ?q=text
 *
 * Old routes /support and /chat redirect here with mapped params.
 */
@Page("/inbox")
@Composable
fun InboxPage() {
    val container = LocalAppContainer.current
    val adminRepo = container.adminRepository
    val meRepo = container.meRepository ?: return

    var activeTab by remember { mutableStateOf(InboxTab.fromCode(urlParam("tab"))) }

    // ── Support state ──────────────────────────────────────────────────────────
    var supportOffset by remember { mutableStateOf(urlParam("page")?.toLongOrNull()?.coerceAtLeast(0L) ?: 0L) }
    var supportQuery by remember { mutableStateOf(urlParam("q") ?: "") }
    var supportData by remember { mutableStateOf<SupportMessagesPage?>(null) }
    val supportLoadState = rememberLoadState()
    var selectedMsg by remember { mutableStateOf<SupportMessageRow?>(null) }

    // ── Chat state ─────────────────────────────────────────────────────────────
    var chatScope by remember {
        mutableStateOf(if (urlParam("scope") == "all" && adminRepo != null) InboxChatScope.ALL else InboxChatScope.MY)
    }
    var chatPage by remember { mutableStateOf(urlParam("page")?.toIntOrNull()?.coerceAtLeast(1) ?: 1) }
    var chatQuery by remember { mutableStateOf(urlParam("q") ?: "") }
    var chatData by remember { mutableStateOf<ChatListPage?>(null) }
    val chatLoadState = rememberLoadState()

    var selectedChat by remember { mutableStateOf<ChatListItem?>(null) }
    var chatOpenToken by remember { mutableStateOf(0) }
    var msgPage by remember { mutableStateOf(1) }
    var msgs by remember { mutableStateOf<ChatMessagesPage?>(null) }
    var msgsError by remember { mutableStateOf<String?>(null) }

    // ── URL sync ───────────────────────────────────────────────────────────────
    // Keys include the per-tab filter/page state so the URL stays live while the
    // user searches/paginates (refresh keeps the position), not only on tab switch.
    LaunchedEffect(activeTab, supportOffset, supportQuery, chatPage, chatQuery, chatScope) {
        if (activeTab == InboxTab.SUPPORT) {
            setUrlParams(
                "tab" to null,
                "page" to supportOffset.takeIf { it > 0L }?.toString(),
                "q" to supportQuery.ifBlank { null },
                "scope" to null,
            )
        } else {
            setUrlParams(
                "tab" to InboxTab.CHATS.code,
                "page" to chatPage.takeIf { it > 1 }?.toString(),
                "q" to chatQuery.ifBlank { null },
                "scope" to "all".takeIf { chatScope == InboxChatScope.ALL },
            )
        }
    }

    // ── Support data loading ───────────────────────────────────────────────────
    LaunchedEffect(supportOffset, supportLoadState.reloadToken) {
        if (activeTab != InboxTab.SUPPORT) return@LaunchedEffect
        if (adminRepo == null) return@LaunchedEffect
        supportData = null
        supportLoadState.load {
            supportData = adminRepo.getSupportMessages(INBOX_SUPPORT_PAGE_SIZE, supportOffset)
        }
    }

    // Reload when switching to support tab
    LaunchedEffect(activeTab) {
        if (activeTab != InboxTab.SUPPORT) return@LaunchedEffect
        if (adminRepo == null) return@LaunchedEffect
        supportData = null
        supportLoadState.load {
            supportData = adminRepo.getSupportMessages(INBOX_SUPPORT_PAGE_SIZE, supportOffset)
        }
    }

    val supportFiltered = supportData?.items?.filter {
        supportQuery.isBlank() ||
            it.email.contains(supportQuery, ignoreCase = true) ||
            it.message.contains(supportQuery, ignoreCase = true) ||
            it.subject?.contains(supportQuery, ignoreCase = true) == true
    }.orEmpty()

    // ── Chat data loading ──────────────────────────────────────────────────────
    LaunchedEffect(chatScope, chatPage, chatQuery, chatLoadState.reloadToken, activeTab) {
        if (activeTab != InboxTab.CHATS) return@LaunchedEffect
        chatData = null
        chatLoadState.load {
            chatData = when (chatScope) {
                InboxChatScope.MY -> meRepo.getMyChats(chatPage, INBOX_CHAT_PAGE_SIZE, chatQuery)
                InboxChatScope.ALL -> (adminRepo ?: error("Admin access required")).getAllChats(chatPage, INBOX_CHAT_PAGE_SIZE, chatQuery)
            }
        }
    }

    // Load messages for selected conversation
    LaunchedEffect(selectedChat?.id, msgPage, chatOpenToken) {
        val chat = selectedChat ?: return@LaunchedEffect
        val repo = adminRepo ?: return@LaunchedEffect
        msgs = null
        msgsError = null
        try {
            msgs = repo.getChatMessages(chat.id, msgPage, 50)
        } catch (e: CancellationException) {
            throw e
        } catch (@Suppress("TooGenericExceptionCaught") e: Throwable) {
            msgsError = e.message ?: "Не удалось загрузить сообщения"
        }
    }

    AdminLayout(
        title = "Входящие",
        subtitle = when (activeTab) {
            InboxTab.SUPPORT -> "Обращения пользователей из приложения и сайта"
            InboxTab.CHATS -> when (chatScope) {
                InboxChatScope.MY -> "Мои переписки и обращения Contact-us"
                InboxChatScope.ALL -> "Все чаты на платформе · только чтение"
            }
        },
        actions = {
            // Search input varies per tab
            when (activeTab) {
                InboxTab.SUPPORT -> {
                    SearchInput(
                        value = supportQuery,
                        onValueChange = { supportQuery = it },
                        placeholderText = "Поиск по email, теме или тексту…",
                    )
                }
                InboxTab.CHATS -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        if (adminRepo != null) {
                            InboxChatScopeToggle(chatScope) { chatScope = it; chatPage = 1 }
                            HSpace(12)
                        }
                        SearchInput(chatQuery, { chatQuery = it; chatPage = 1 }, "Поиск по чату или пользователю")
                    }
                }
            }
        },
    ) {
        // ── Page header: Unbounded title + tab switcher ──────────────────────
        Row(
            Modifier.fillMaxWidth()
                .classNames("na-page-header")
                .styleModifier {
                    property("align-items", "flex-end")
                    property("justify-content", "space-between")
                    property("gap", "20px")
                }
        ) {
            SpanText(
                "Входящие",
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-size", "24px")
                    property("font-weight", "700")
                    property("letter-spacing", "-0.02em")
                    property("color", "#F0F4FC")
                }
            )

            Box(Modifier.classNames("na-tab-switcher")) {
            InboxTabSwitcher(
                active = activeTab,
                onSelect = { tab -> activeTab = tab }
            )
            }
        }

        VSpace(20)

        // ── Tab content — fade-in on switch ──────────────────────────────────
        key(activeTab) {
            Box(Modifier.fillMaxWidth().classNames("na-fade-in")) {
                Column(Modifier.fillMaxWidth()) {
                    when (activeTab) {
                        InboxTab.SUPPORT -> {
                            InboxSupportTabContent(
                                adminRepo = adminRepo,
                                loadState = supportLoadState,
                                offset = supportOffset,
                                query = supportQuery,
                                data = supportData,
                                filtered = supportFiltered,
                                onOffsetChange = { supportOffset = it },
                                onQueryChange = { supportQuery = it },
                                onSelectMsg = { selectedMsg = it },
                            )
                        }
                        InboxTab.CHATS -> {
                            InboxChatsTabContent(
                                meRepo = meRepo,
                                adminRepo = adminRepo,
                                loadState = chatLoadState,
                                scope = chatScope,
                                page = chatPage,
                                query = chatQuery,
                                data = chatData,
                                selectedChat = selectedChat,
                                msgs = msgs,
                                msgsError = msgsError,
                                msgPage = msgPage,
                                onScopeChange = { chatScope = it; chatPage = 1 },
                                onPageChange = { chatPage = it },
                                onQueryChange = { chatQuery = it; chatPage = 1 },
                                onSelectChat = { chat ->
                                    if (selectedChat?.id != chat.id) {
                                        selectedChat = chat
                                        chatOpenToken++
                                        msgPage = 1
                                        msgs = null
                                        msgsError = null
                                    }
                                },
                                onMsgPageOlder = { msgs?.let { if (msgPage < it.totalPages) msgPage++ } },
                                onMsgPageNewer = { if (msgPage > 1) msgPage-- },
                            )
                        }
                    }
                }
            }
        }

        VSpace(40)
    }

    // ── Support detail drawer ──────────────────────────────────────────────────
    val msg = selectedMsg
    Drawer(
        open = msg != null,
        onClose = { selectedMsg = null },
        title = "Обращение",
    ) {
        if (msg != null) {
            InboxSupportDetailDrawerContent(msg = msg)
        }
    }
}

// ── Tab switcher ──────────────────────────────────────────────────────────────

@Composable
private fun InboxTabSwitcher(active: InboxTab, onSelect: (InboxTab) -> Unit) {
    Row(
        Modifier.styleModifier {
            property("display", "flex")
            property("gap", "2px")
            property("background", "rgba(255,255,255,.05)")
            property("border", "1px solid rgba(255,255,255,.08)")
            property("border-radius", "12px")
            property("padding", "3px")
        }
    ) {
        InboxTab.entries.forEach { tab ->
            InboxTabChip(
                label = tab.label,
                active = active == tab,
                onClick = { onSelect(tab) }
            )
        }
    }
}

@Composable
private fun InboxTabChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .styleModifier {
                property("font-size", "13px")
                property("padding", "6px 16px")
                property("border-radius", "9px")
                property("cursor", "pointer")
                property("user-select", "none")
                property("transition", "all 160ms ease")
                property("white-space", "nowrap")
                property("font-family", "'Inter', sans-serif")
                property("font-weight", if (active) "600" else "400")
                if (active) {
                    property("background", "rgba(0,97,255,.30)")
                    property("color", "#8FB8FF")
                } else {
                    property("color", "rgba(240,244,252,.50)")
                }
            }
            .classNames("na-press")
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: ОБРАЩЕНИЯ (Support)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun InboxSupportTabContent(
    adminRepo: com.arenaproplay.admin.domain.admin.AdminRepository?,
    loadState: LoadState,
    offset: Long,
    query: String,
    data: SupportMessagesPage?,
    filtered: List<SupportMessageRow>,
    onOffsetChange: (Long) -> Unit,
    onQueryChange: (String) -> Unit,
    onSelectMsg: (SupportMessageRow) -> Unit,
) {
    if (adminRepo == null) {
        GlassCard {
            EmptyState(
                icon = "🔒",
                title = "Нет доступа",
                hint = "Обращения доступны только администраторам",
            )
        }
        return
    }

    if (loadState.error != null) {
        ErrorCard(loadState)
        return
    }

    // Result count
    val totalLoaded = data?.total ?: 0L
    if (data != null) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                if (query.isNotBlank())
                    "Найдено: ${filtered.size} из ${data.items.size}"
                else
                    "Всего: $totalLoaded обращений",
                Modifier.bodySMedium().color(AppColors.Muted)
            )
        }
        VSpace(10)
    }

    // Inbox SectionCard — dark glass surface
    SectionCard(
        title = "Входящие",
        subtitle = "Обращения сортируются по дате — новые сверху",
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (data == null) {
            repeat(6) { idx -> InboxSupportSkeletonRow(isLast = idx == 5) }
        } else if (filtered.isEmpty()) {
            EmptyState(
                icon = "💬",
                title = "Пока нет обращений",
                hint = if (query.isNotBlank())
                    "Ничего не найдено по запросу «$query»"
                else
                    "Новые обращения от пользователей появятся здесь",
            )
        } else {
            filtered.forEachIndexed { idx, msg ->
                InboxSupportRow(
                    msg = msg,
                    isLast = idx == filtered.lastIndex,
                    onClick = { onSelectMsg(msg) },
                )
            }
        }
    }

    VSpace(16)

    // Pager
    InboxSupportPager(
        offset = offset,
        count = filtered.size,
        total = data?.total ?: 0L,
        onPrev = { onOffsetChange((offset - INBOX_SUPPORT_PAGE_SIZE).coerceAtLeast(0)) },
        onNext = {
            if (offset + INBOX_SUPPORT_PAGE_SIZE < (data?.total ?: 0L))
                onOffsetChange(offset + INBOX_SUPPORT_PAGE_SIZE)
        },
    )

    VSpace(28)
}

// ── Support inbox row ──────────────────────────────────────────────────────────

private fun inboxSenderInitials(email: String): String {
    val local = email.substringBefore("@").trim()
    val words = local.split(".", "_", "-").filter { it.isNotEmpty() }
    return when {
        words.size >= 2 -> "${words[0].first().uppercaseChar()}${words[1].first().uppercaseChar()}"
        local.length >= 2 -> "${local[0].uppercaseChar()}${local[1].uppercaseChar()}"
        local.isNotEmpty() -> local.first().uppercaseChar().toString()
        else -> "?"
    }
}

private fun inboxSubjectPillTone(subject: String): PillTone {
    val s = subject.lowercase()
    return when {
        s.contains("bug") || s.contains("cheater") || s.contains("error") -> PillTone.Danger
        s.contains("payment") || s.contains("subscription") || s.contains("billing") -> PillTone.Accent
        s.contains("account") || s.contains("rating") || s.contains("ban") -> PillTone.Warning
        else -> PillTone.Neutral
    }
}

@Composable
private fun InboxSupportRow(
    msg: SupportMessageRow,
    isLast: Boolean,
    onClick: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 4.px, topBottom = 12.px)
            .classNames("arena-table-row")
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
                property("transition", "background 140ms ease")
                property("border-radius", "10px")
            }
            .onClick { onClick() },
        verticalAlignment = Alignment.Top,
    ) {
        AvatarBubble(seed = msg.email, initials = inboxSenderInitials(msg.email), sizePx = 40)
        HSpace(12)
        Column(Modifier.weight(1f)) {
            // Row 1: sender + time
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    msg.email,
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .weight(1f)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                )
                HSpace(12)
                SpanText(
                    formatTimestampShort(msg.createdAt),
                    Modifier.bodySMedium().color(AppColors.Faint)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("flex-shrink", "0")
                            property("font-size", "11px")
                        }
                )
            }

            // Row 2: subject pill + userId tag
            val subject = msg.subject
            val userId = msg.userId
            if (subject != null || userId != null) {
                Box(Modifier.height(4.px))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (subject != null) {
                        Pill(text = subject, tone = inboxSubjectPillTone(subject))
                    }
                    if (userId != null) {
                        if (subject != null) HSpace(6)
                        Box(
                            Modifier
                                .padding(leftRight = 8.px, topBottom = 2.px)
                                .sBorderRadius(999)
                                .styleModifier {
                                    property("background", "rgba(255,255,255,.06)")
                                }
                        ) {
                            SpanText(
                                "#${userId.take(8)}",
                                Modifier.bodySMedium().color(AppColors.Faint)
                                    .styleModifier { property("font-size", "12px") }
                            )
                        }
                    }
                }
            }

            // Row 3: message preview (2 lines)
            Box(Modifier.height(4.px))
            SpanText(
                msg.message,
                Modifier.bodyMRegular().color(AppColors.Muted)
                    .styleModifier {
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                        property("word-break", "break-word")
                    }
            )

            // Attachment indicator
            if (msg.attachmentUrl != null) {
                Box(Modifier.height(4.px))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText("📎", Modifier.fontSize(11.px))
                    HSpace(4)
                    SpanText(
                        "Вложение",
                        Modifier.bodySMedium().color(AppColors.Primary)
                            .styleModifier { property("font-size", "11px") }
                    )
                }
            }
        }
    }
}

// ── Support skeleton row ───────────────────────────────────────────────────────

@Composable
private fun InboxSupportSkeletonRow(isLast: Boolean) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 12.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
            },
        verticalAlignment = Alignment.Top,
    ) {
        Box(
            Modifier.width(40.px).height(40.px).borderRadius(50.percent)
                .styleModifier {
                    property("background", "rgba(255,255,255,.05)")
                    property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    property("flex-shrink", "0")
                }
        )
        HSpace(12)
        Column(Modifier.weight(1f)) {
            Box(
                Modifier.height(13.px).sBorderRadius(6)
                    .styleModifier {
                        property("width", "55%")
                        property("background", "rgba(255,255,255,.05)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
            Box(Modifier.height(8.px))
            Box(
                Modifier.height(11.px).sBorderRadius(6)
                    .styleModifier {
                        property("width", "80%")
                        property("background", "rgba(255,255,255,.03)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
            Box(Modifier.height(6.px))
            Box(
                Modifier.height(11.px).sBorderRadius(6)
                    .styleModifier {
                        property("width", "65%")
                        property("background", "rgba(255,255,255,.03)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
        }
    }
}

// ── Support detail drawer content ──────────────────────────────────────────────

@Composable
private fun InboxSupportDetailDrawerContent(msg: SupportMessageRow) {
    Column(Modifier.fillMaxWidth()) {
        // Sender card — dark glass
        Row(
            Modifier
                .fillMaxWidth()
                .sBorderRadius(12)
                .sPadding(14)
                .styleModifier {
                    property("background", "rgba(255,255,255,.04)")
                    property("border", "1px solid rgba(255,255,255,.08)")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AvatarBubble(seed = msg.email, initials = inboxSenderInitials(msg.email), sizePx = 48)
            HSpace(12)
            Column(Modifier.weight(1f)) {
                SpanText(
                    msg.email,
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .styleModifier {
                            property("font-size", "14px")
                            property("font-weight", "700")
                            property("word-break", "break-all")
                        }
                )
                Box(Modifier.height(3.px))
                SpanText(
                    formatTimestampShort(msg.createdAt),
                    Modifier.bodySMedium().color(AppColors.Muted)
                )
            }
        }

        VSpace(16)

        if (msg.subject != null) {
            InboxSupportMetaRow("Тема") {
                Pill(text = msg.subject, tone = inboxSubjectPillTone(msg.subject))
            }
        }
        if (msg.userId != null) {
            InboxSupportMetaRow("User ID") {
                SpanText(
                    "#${msg.userId.take(12)}",
                    Modifier.bodySMedium().color(AppColors.Muted)
                        .styleModifier { property("font-family", "'JetBrains Mono', monospace") }
                )
            }
        }

        VSpace(16)

        // Message body label (eyebrow)
        SpanText(
            "Сообщение",
            Modifier.bodySMedium().color(AppColors.Faint)
                .styleModifier {
                    property("font-size", "11px")
                    property("font-weight", "600")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                }
        )
        Box(Modifier.height(8.px))
        // Message body block — dark glass
        Box(
            Modifier
                .fillMaxWidth()
                .sBorderRadius(10)
                .sPadding(14)
                .styleModifier {
                    property("background", "rgba(255,255,255,.04)")
                    property("border", "1px solid rgba(255,255,255,.08)")
                }
        ) {
            SpanText(
                msg.message,
                Modifier.bodyMRegular().color(AppColors.Ink)
                    .styleModifier {
                        property("white-space", "pre-wrap")
                        property("word-break", "break-word")
                        property("line-height", "1.6")
                    }
            )
        }

        // Attachment action
        if (msg.attachmentUrl != null) {
            VSpace(16)
            Box(
                Modifier
                    .fillMaxWidth()
                    .sBorderRadius(10)
                    .padding(leftRight = 16.px, topBottom = 12.px)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("background", "rgba(0,97,255,.12)")
                        property("border", "1px solid rgba(0,97,255,.25)")
                        property("transition", "opacity 140ms ease")
                    }
                    .onClick {
                        window.open(msg.attachmentUrl, "_blank", "noopener,noreferrer")
                    },
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText("📎", Modifier.fontSize(14.px))
                    HSpace(8)
                    SpanText(
                        "Открыть вложение",
                        Modifier.bodySMedium().color(AppColors.Primary)
                            .styleModifier { property("font-weight", "600") }
                    )
                }
            }
        }
    }
}

@Composable
private fun InboxSupportMetaRow(label: String, value: @Composable () -> Unit) {
    Row(
        Modifier.fillMaxWidth()
            .padding(topBottom = 7.px)
            .styleModifier {
                property("border-bottom", "1px solid rgba(255,255,255,.06)")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            label,
            Modifier.bodySMedium().color(AppColors.Faint)
                .styleModifier {
                    property("width", "100px")
                    property("flex-shrink", "0")
                    property("font-size", "11.5px")
                }
        )
        value()
    }
}

// ── Support pager ──────────────────────────────────────────────────────────────

@Composable
private fun InboxSupportPager(
    offset: Long,
    count: Int,
    total: Long,
    onPrev: () -> Unit,
    onNext: () -> Unit,
) {
    val from = if (count == 0) 0L else offset + 1
    val to = offset + count
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        SpanText(
            "$from–$to из $total",
            Modifier.bodySMedium().color(AppColors.Muted),
        )
        Box(Modifier.weight(1f))
        PagerButton("‹ Новее", onPrev, offset > 0)
        HSpace(8)
        PagerButton("Старше ›", onNext, offset + INBOX_SUPPORT_PAGE_SIZE < total)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2: ЧАТЫ (Chat)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun InboxChatsTabContent(
    meRepo: com.arenaproplay.admin.domain.me.MeRepository,
    adminRepo: com.arenaproplay.admin.domain.admin.AdminRepository?,
    loadState: LoadState,
    scope: InboxChatScope,
    page: Int,
    query: String,
    data: ChatListPage?,
    selectedChat: ChatListItem?,
    msgs: ChatMessagesPage?,
    msgsError: String?,
    msgPage: Int,
    onScopeChange: (InboxChatScope) -> Unit,
    onPageChange: (Int) -> Unit,
    onQueryChange: (String) -> Unit,
    onSelectChat: (ChatListItem) -> Unit,
    onMsgPageOlder: () -> Unit,
    onMsgPageNewer: () -> Unit,
) {
    if (loadState.error != null) {
        ErrorCard(loadState)
        return
    }

    // Mobile: when a chat is selected, show only the thread pane.
    // mobileShowThread tracks this state — true when selectedChat is set and user is on narrow screen.
    // We use a simple boolean driven by selection; the back button clears the selection.
    var mobileShowThread by remember { mutableStateOf(false) }
    // When chat selected, switch to thread view on mobile
    LaunchedEffect(selectedChat) {
        if (selectedChat != null) mobileShowThread = true
    }

    // Two-pane grid: list left + thread right
    Div(attrs = Modifier
            .fillMaxWidth()
            .classNames("arena-chat-grid")
            .toAttrs()
    ) {
        // ── Left: conversation list ────────────────────────────────────────
        // Hidden on mobile when thread is shown (.na-chat-list-hide class via CSS)
        SectionCard(
            title = "Чаты",
            subtitle = null,
            modifier = Modifier
                .classNames(if (mobileShowThread) "na-chat-list-hide" else "na-chat-list-show")
                .styleModifier { property("min-height", "520px") }
        ) {
            if (data == null) {
                repeat(6) { idx -> InboxChatSkeletonRow(isLast = idx == 5) }
            } else if (data.items.isEmpty()) {
                EmptyState(
                    icon = "💬",
                    title = "Нет чатов",
                    hint = if (query.isNotBlank())
                        "Ничего не найдено по запросу «$query»"
                    else
                        "Здесь появятся переписки",
                )
            } else {
                data.items.forEachIndexed { idx, chat ->
                    InboxChatConversationRow(
                        chat = chat,
                        scope = scope,
                        isSelected = selectedChat?.id == chat.id,
                        isLast = idx == data.items.lastIndex,
                        // Прямой сброс mobileShowThread: LaunchedEffect(selectedChat)
                        // не перезапустится при тапе по ТОМУ ЖЕ чату после «Назад».
                        onClick = if (adminRepo != null) { { onSelectChat(chat); mobileShowThread = true } } else null,
                    )
                }
            }

            // Pager inside left pane
            if (data != null && (data.totalPages > 1 || page > 1)) {
                VSpace(12)
                InboxChatListPager(
                    page = page,
                    data = data,
                    onPrev = { if (page > 1) onPageChange(page - 1) },
                    onNext = { if (page < (data.totalPages)) onPageChange(page + 1) },
                )
            }
        }

        // ── Right: thread view ─────────────────────────────────────────────
        // Hidden on mobile when no chat is selected (.na-chat-thread-hide class via CSS)
        SectionCard(
            title = selectedChat?.let { it.name ?: inboxChatFallbackName(it) } ?: "Переписка",
            subtitle = selectedChat?.let { inboxChatSubtitleFor(it, scope) },
            modifier = Modifier
                .classNames(if (mobileShowThread) "na-chat-thread-show" else "na-chat-thread-hide")
                .styleModifier { property("min-height", "520px") }
        ) {
            // Mobile «← Назад» button — visible only on narrow screens via CSS
            if (selectedChat != null) {
                Box(
                    Modifier
                        .classNames("na-chat-back-btn")
                        .styleModifier {
                            property("display", "none") // shown via CSS below 1000px
                            property("align-items", "center")
                            property("gap", "6px")
                            property("cursor", "pointer")
                            property("padding", "8px 0 12px 0")
                            property("color", "#8FB8FF")
                            property("font-size", "13px")
                            property("font-weight", "500")
                            property("font-family", "'Inter', sans-serif")
                        }
                        .onClick { mobileShowThread = false }
                ) {
                    SpanText("← Назад")
                }
            }

            val chat = selectedChat
            when {
                chat == null -> {
                    EmptyState(
                        icon = "🗨️",
                        title = "Выберите чат",
                        hint = "Нажмите на переписку слева, чтобы увидеть сообщения",
                    )
                }
                adminRepo == null -> {
                    EmptyState(
                        icon = "🔒",
                        title = "Нет доступа",
                        hint = "Просмотр переписок доступен только администраторам",
                    )
                }
                else -> {
                    InboxChatThreadView(
                        page = msgs,
                        error = msgsError,
                        currentPage = msgPage,
                        onOlder = onMsgPageOlder,
                        onNewer = onMsgPageNewer,
                    )
                }
            }
        }
    }

    VSpace(28)
}

// ── Chat conversation row ──────────────────────────────────────────────────────

@Composable
private fun InboxChatConversationRow(
    chat: ChatListItem,
    scope: InboxChatScope,
    isSelected: Boolean,
    isLast: Boolean,
    onClick: (() -> Unit)?,
) {
    val hasUnread = scope == InboxChatScope.MY && chat.unreadCount > 0

    Row(
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 4.px, topBottom = 12.px)
            .classNames("arena-table-row")
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer) else Modifier)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
                property("border-radius", "10px")
                property("transition", "background 140ms ease")
                if (isSelected) property("background", "rgba(0,97,255,.15)")
            }
            .then(if (onClick != null) Modifier.onClick { onClick() } else Modifier),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        InboxChatConversationAvatar(chat)
        HSpace(12)
        Column(Modifier.weight(1f)) {
            // Row 1: name + time
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    chat.name ?: inboxChatFallbackName(chat),
                    Modifier.bodyMMedium()
                        .color(AppColors.Ink)
                        .weight(1f)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            if (hasUnread) property("font-weight", "700")
                        }
                )
                HSpace(8)
                if (chat.isMuted) {
                    SpanText("🔕", Modifier.fontSize(11.px))
                    HSpace(4)
                }
                SpanText(
                    formatTimestampShort(
                        chat.lastMessage?.createdAt?.takeIf { it.isNotBlank() } ?: chat.createdAt
                    ),
                    Modifier.mono(10).color(AppColors.Faint)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("flex-shrink", "0")
                        }
                )
            }

            // Row 2: type pill + privacy chip
            Box(Modifier.height(4.px))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Pill(inboxPrettifyChatType(chat.type), inboxChatTypePillTone(chat.type))
                HSpace(6)
                Box(
                    Modifier
                        .padding(leftRight = 8.px, topBottom = 2.px)
                        .sBorderRadius(999)
                        .styleModifier { property("background", "rgba(255,255,255,.05)") }
                ) {
                    SpanText(
                        chat.privacy.lowercase().replaceFirstChar { it.uppercaseChar() },
                        Modifier.bodySMedium().color(AppColors.Faint)
                            .styleModifier { property("font-size", "12px") }
                    )
                }
                Box(Modifier.weight(1f))
                if (hasUnread) {
                    InboxChatUnreadBadge(chat.unreadCount)
                }
            }

            // Row 3: last message preview
            val lastMsg = chat.lastMessage
            if (lastMsg != null) {
                Box(Modifier.height(4.px))
                SpanText(
                    inboxSnippet(lastMsg.content),
                    Modifier.bodySMedium().color(if (hasUnread) AppColors.Ink else AppColors.Muted)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            if (hasUnread) property("font-weight", "500")
                        }
                )
            }
        }
    }
}

@Composable
private fun InboxChatConversationAvatar(chat: ChatListItem) {
    val isDirect = chat.type.equals("DIRECT", true)
    val gradient = AppColors.AvatarPalette[kotlin.math.abs(chat.id.hashCode()) % AppColors.AvatarPalette.size]

    Box(
        Modifier
            .width(44.px)
            .minWidth(44.px)
            .height(44.px)
            .sBorderRadius(if (isDirect) 999 else 14)
            .styleModifier {
                if (chat.imageUrl != null) {
                    property("background-image", "url('${chat.imageUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                } else {
                    property("background", gradient)
                }
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        if (chat.imageUrl == null) {
            val initials = inboxChatInitials(chat.name)
            SpanText(
                initials,
                Modifier.bodySMedium()
                    .styleModifier {
                        property("color", "#05080F")
                        property("font-weight", "700")
                        property("font-size", "15px")
                        property("line-height", "1")
                    }
            )
        }
    }
}

// ── Chat thread view ───────────────────────────────────────────────────────────

@Composable
private fun InboxChatThreadView(
    page: ChatMessagesPage?,
    error: String?,
    currentPage: Int,
    onOlder: () -> Unit,
    onNewer: () -> Unit,
) {
    when {
        error != null -> {
            ErrorState(message = error, onRetry = {})
        }
        page == null -> {
            Box(Modifier.fillMaxWidth().height(300.px), contentAlignment = Alignment.Center) {
                com.arenaproplay.admin.ui.components.widgets.CardSpinner()
            }
        }
        page.messages.isEmpty() -> {
            EmptyState(
                icon = "🗨️",
                title = "Нет сообщений",
                hint = "В этом чате пока нет сообщений",
            )
        }
        else -> {
            Column(
                Modifier
                    .fillMaxWidth()
                    .maxHeight(480.px)
                    .overflow(Overflow.Auto)
                    .padding(right = 6.px),
            ) {
                page.messages.asReversed().forEachIndexed { idx, message ->
                    InboxChatMessageBubble(
                        message = message,
                        isLast = idx == page.messages.lastIndex,
                    )
                }
            }
        }
    }

    if (page != null && page.totalPages > 1) {
        VSpace(12)
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            PagerButton("‹ Старше", onOlder, currentPage < page.totalPages)
            HSpace(8)
            PagerButton("Новее ›", onNewer, currentPage > 1)
            Box(Modifier.weight(1f))
            SpanText(
                "Страница $currentPage из ${page.totalPages} · ${page.total} сообщений",
                Modifier.mono(10).color(AppColors.Faint),
            )
        }
    }
}

// ── Chat message bubble ────────────────────────────────────────────────────────

@Composable
private fun InboxChatMessageBubble(message: ChatMessage, isLast: Boolean) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 8.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.04)")
            },
        verticalAlignment = Alignment.Top,
    ) {
        // Sender avatar
        val initials = (message.senderUsername ?: "?").firstOrNull()?.uppercase() ?: "?"
        Box(
            Modifier
                .width(32.px)
                .minWidth(32.px)
                .height(32.px)
                .borderRadius(50.percent)
                .styleModifier {
                    if (message.senderAvatarUrl != null) {
                        property("background-image", "url('${message.senderAvatarUrl}')")
                        property("background-size", "cover")
                        property("background-position", "center")
                    } else {
                        val idx = kotlin.math.abs((message.senderId.ifBlank { "?" }).hashCode()) %
                            AppColors.AvatarPalette.size
                        property("background", AppColors.AvatarPalette[idx])
                    }
                    property("flex-shrink", "0")
                },
            contentAlignment = Alignment.Center,
        ) {
            if (message.senderAvatarUrl == null) {
                SpanText(
                    initials,
                    Modifier.styleModifier {
                        property("color", "#05080F")
                        property("font-weight", "700")
                        property("font-size", "11px")
                        property("line-height", "1")
                    }
                )
            }
        }

        HSpace(10)

        Column(Modifier.weight(1f)) {
            // Sender name + timestamp
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    message.senderUsername ?: "Неизвестный",
                    Modifier.bodySMedium().color(AppColors.Ink)
                        .styleModifier { property("font-weight", "600") }
                )
                HSpace(8)
                SpanText(
                    formatTimestampShort(message.createdAt),
                    Modifier.mono(10).color(AppColors.Faint),
                )
            }

            Box(Modifier.height(4.px))

            // Message bubble — dark glass
            Box(
                Modifier
                    .sBorderRadius(10)
                    .sPadding(10, 14)
                    .styleModifier {
                        property("background", "rgba(255,255,255,.05)")
                        property("border", "1px solid rgba(255,255,255,.08)")
                        property("display", "inline-block")
                        property("max-width", "100%")
                    }
            ) {
                InboxChatMessageContent(message)
            }
        }
    }
}

@Composable
private fun InboxChatMessageContent(message: ChatMessage) {
    when (message.type.uppercase()) {
        "IMAGE" -> {
            Box(
                Modifier
                    .width(180.px)
                    .height(130.px)
                    .sBorderRadius(8)
                    .styleModifier {
                        property("background", "rgba(255,255,255,.06)")
                        if (message.fileUrl != null) {
                            property("background-image", "url('${message.fileUrl}')")
                            property("background-size", "cover")
                            property("background-position", "center")
                        }
                    }
            )
        }
        "TEXT" -> {
            SpanText(
                message.content,
                Modifier.bodyMRegular().color(AppColors.Ink)
                    .styleModifier {
                        property("word-break", "break-word")
                        property("white-space", "pre-wrap")
                        property("line-height", "1.55")
                    }
            )
        }
        else -> {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Pill(inboxPrettifyChatType(message.type), PillTone.Neutral)
                val label = message.fileName ?: message.content.takeIf { it.isNotBlank() }
                if (label != null) {
                    HSpace(8)
                    SpanText(label, Modifier.bodySMedium().color(AppColors.Muted))
                }
            }
        }
    }
}

// ── Chat skeleton row ──────────────────────────────────────────────────────────

@Composable
private fun InboxChatSkeletonRow(isLast: Boolean) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 12.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
            },
        verticalAlignment = Alignment.Top,
    ) {
        Box(
            Modifier.width(44.px).minWidth(44.px).height(44.px).sBorderRadius(14)
                .styleModifier {
                    property("background", "rgba(255,255,255,.05)")
                    property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    property("flex-shrink", "0")
                }
        )
        HSpace(12)
        Column(Modifier.weight(1f)) {
            Box(
                Modifier.height(13.px).sBorderRadius(6)
                    .styleModifier {
                        property("width", "60%")
                        property("background", "rgba(255,255,255,.05)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
            Box(Modifier.height(7.px))
            Box(
                Modifier.height(11.px).sBorderRadius(6)
                    .styleModifier {
                        property("width", "40%")
                        property("background", "rgba(255,255,255,.03)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
            Box(Modifier.height(6.px))
            Box(
                Modifier.height(11.px).sBorderRadius(6)
                    .styleModifier {
                        property("width", "80%")
                        property("background", "rgba(255,255,255,.03)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
        }
    }
}

// ── Chat scope toggle ──────────────────────────────────────────────────────────

@Composable
private fun InboxChatScopeToggle(current: InboxChatScope, onChange: (InboxChatScope) -> Unit) {
    Row(
        Modifier
            .sBorderRadius(999)
            .styleModifier {
                property("background", "rgba(255,255,255,.04)")
                property("border", "1px solid rgba(255,255,255,.08)")
                property("padding", "4px")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        InboxChatScopeChip("Мои чаты", current == InboxChatScope.MY) { onChange(InboxChatScope.MY) }
        InboxChatScopeChip("Все чаты", current == InboxChatScope.ALL) { onChange(InboxChatScope.ALL) }
    }
}

@Composable
private fun InboxChatScopeChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .padding(leftRight = 16.px, topBottom = 7.px)
            .sBorderRadius(999)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (active) {
                    property("background", "rgba(0,97,255,.25)")
                    property("box-shadow", "0 1px 2px rgba(0,0,0,.20)")
                }
                property("transition", "all 180ms ease")
            }
            .onClick { onClick() },
    ) {
        SpanText(
            label,
            Modifier.bodySMedium().color(if (active) AppColors.Primary else AppColors.Muted)
                .styleModifier { if (active) property("font-weight", "600") },
        )
    }
}

// ── Chat unread badge ──────────────────────────────────────────────────────────

@Composable
private fun InboxChatUnreadBadge(count: Long) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 8.px, topBottom = 3.px)
            .styleModifier { property("background", AppColors.Primary.toRgbString()) }
    ) {
        SpanText(
            count.toString(),
            Modifier.mono(10).styleModifier {
                property("color", "#05080F")
                property("font-weight", "700")
            }
        )
    }
}

// ── Chat list pager ────────────────────────────────────────────────────────────

@Composable
private fun InboxChatListPager(
    page: Int,
    data: ChatListPage?,
    onPrev: () -> Unit,
    onNext: () -> Unit,
) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        SpanText(
            "Стр. ${data?.page ?: page} из ${data?.totalPages ?: "—"} · ${data?.totalItems ?: 0} чатов",
            Modifier.mono(10).color(AppColors.Muted),
        )
        Box(Modifier.weight(1f))
        PagerButton("‹", onPrev, page > 1)
        HSpace(6)
        PagerButton("›", { if (page < (data?.totalPages ?: 1)) onNext() }, page < (data?.totalPages ?: 1))
    }
}

// ── Chat helper functions ──────────────────────────────────────────────────────

private fun inboxChatInitials(name: String?): String {
    if (name.isNullOrBlank()) return "#"
    val words = name.trim().split(" ", ".", "_", "-").filter { it.isNotEmpty() }
    return when {
        words.size >= 2 -> "${words[0].first().uppercaseChar()}${words[1].first().uppercaseChar()}"
        name.length >= 2 -> "${name[0].uppercaseChar()}${name[1].uppercaseChar()}"
        name.isNotEmpty() -> name.first().uppercaseChar().toString()
        else -> "#"
    }
}

private fun inboxChatFallbackName(row: ChatListItem): String = when (row.type.uppercase()) {
    "DIRECT" -> "Личный чат #${row.id.take(8)}"
    "GROUP" -> "Группа #${row.id.take(8)}"
    "EVENT" -> "Чат события #${row.id.take(8)}"
    else -> "Чат #${row.id.take(8)}"
}

private fun inboxChatSubtitleFor(row: ChatListItem, scope: InboxChatScope): String {
    val parts = mutableListOf<String>()
    parts += inboxPrettifyChatType(row.type)
    parts += row.privacy.lowercase().replaceFirstChar { it.uppercaseChar() }
    if (scope == InboxChatScope.MY && row.unreadCount > 0) parts += "${row.unreadCount} непрочитанных"
    return parts.joinToString(" · ")
}

private fun inboxPrettifyChatType(t: String): String = when (t.uppercase()) {
    "DIRECT" -> "Личный"
    "GROUP" -> "Группа"
    "EVENT" -> "Событие"
    else -> t.lowercase().replaceFirstChar { it.uppercase() }
}

private fun inboxChatTypePillTone(t: String): PillTone = when (t.uppercase()) {
    "GROUP" -> PillTone.Accent
    "EVENT" -> PillTone.Warning
    else -> PillTone.Neutral
}

private fun inboxSnippet(s: String): String = if (s.length <= 80) s else s.take(80) + "…"
